import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../header/header.component';

// IMPORTANT: Adjust these paths to match your actual service file locations
import { SettingsService } from '../../services/settings.service';
import { LoggerService } from '../../services/logger.service';

export interface PasswordPolicy {
  contain_upper_case?: number;
  contain_lower_case?: number;
  contain_number?: number;
  contain_special_character?: number;
  minimum_length?: number;
  is_default?: number;
  [key: string]: any; // Catch-all for any other properties returned by the API
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent],
  templateUrl: './settings.component.html'
})
export class SettingsComponent implements OnInit {
  copyrightDetails: any;

  // Exact variable names mapped from legacy vm
  passwordTerms: PasswordPolicy = {};
  old_terms: PasswordPolicy = {};
  message: string | null = "";
  errorMessage: string | null = null;
  isLoaded: boolean = false;

  // Injected services
  private settingsService = inject(SettingsService);
  private loggerService = inject(LoggerService);
  private cdr = inject(ChangeDetectorRef);
  private logger = this.loggerService.getInstance("SettingsController");

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      const savedDetails = sessionStorage.getItem('copyrightDetails');
      if (savedDetails) {
        this.copyrightDetails = JSON.parse(savedDetails);
      }
    }
    this.getPasswordPolicy();
  }

  // Added '?' to isCache to fix the TypeScript "Expected 0 arguments" error
  getPasswordPolicy(isCache?: boolean): void {
    this.isLoaded = false;

    if (isCache === undefined || isCache === null) {
      isCache = false;
    }

    this.isLoaded = false;

    this.settingsService.GetPasswordPolicy(isCache)
      .then((response: any) => {
        this.isLoaded = true;
        this.passwordTerms = response.data[0];
        this.old_terms = JSON.parse(JSON.stringify(this.passwordTerms));
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        // Using your exact LoggerService prototype
        this.logger.error('SettingsController Error:', error);
      });
  }

  hasPolicyChanged(payload?: PasswordPolicy): boolean {
    if (payload) {
      if (
        this.old_terms.contain_upper_case !== payload.contain_upper_case ||
        this.old_terms.contain_lower_case !== payload.contain_lower_case ||
        this.old_terms.contain_number !== payload.contain_number ||
        this.old_terms.contain_special_character !== payload.contain_special_character ||
        this.old_terms.minimum_length !== payload.minimum_length
      ) {
        return true;
      }
    } else if (!payload) {
      if (
        this.old_terms.is_default === 0 &&
        (this.old_terms.contain_upper_case === 0 ||
          this.old_terms.contain_lower_case === 0 ||
          this.old_terms.contain_number === 0 ||
          this.old_terms.contain_special_character === 0 ||
          this.old_terms.minimum_length !== 6)
      ) {
        return true;
      }
    }
    return false;
  }

  updatePasswordPolicy(payload: PasswordPolicy): void {
    this.errorMessage = null;
    this.message = null;

    !payload.minimum_length ? (payload.minimum_length = 0) : payload.minimum_length;
    payload.is_default = 0;

    if (this.hasPolicyChanged(payload) === true) {
      this.settingsService.UpdatePasswordPolicy(payload)
        .then((response: any) => {
          this.passwordTerms = payload;
          this.old_terms = payload;
          this.message = "Password policy updated";
          this.getPasswordPolicy();
        })
        .catch((error: any) => {
          // Using your exact LoggerService prototype
          this.logger.error('SettingsController Error:', error);
          this.errorMessage = "Unable to update";
        });
    } else {
      this.errorMessage = "Nothing to update";
    }

    setTimeout(() => {
      this.message = null;
      this.errorMessage = null;
      this.cdr.detectChanges();
    }, 5000);
  }

resetPasswordPolicy(): void {
  this.errorMessage = null;
  this.message = null;

  // Always reset to default values, regardless of current state
  this.settingsService.ResetPasswordPolicy()
    .then((response: any) => {
      this.getPasswordPolicy();
      this.message = "Password policy is reset";
      this.cdr.detectChanges();
    })
    .catch((error: any) => {
      this.errorMessage = "Unable to reset password policy";
      this.logger.error('SettingsController Error:', error);
      this.cdr.detectChanges();
    });

  setTimeout(() => {
    this.message = null;
    this.errorMessage = null;
    this.cdr.detectChanges();
  }, 5000);
}
}